module Visonum.API.SpeedMaster
  ( BulkPingCancelRequest
  , BulkPingCancelResponse
  , BulkPingConfig
  , BulkPingEntry
  , BulkPingJobStatus(..)
  , BulkPingJobStatusEntry
  , BulkPingResult(..)
  , BulkPingResultQuery
  , BulkPingResultResponse
  , BulkPingStartRequest
  , BulkPingStartResponse
  , BulkPingStatusResponse
  , IP
  , UUIDv4
  , codecBulkPingResultResponse
  , codecBulkPingStartRequest
  , codecBulkPingStatusResponse
  , codecJobStatusEntry
  , getBulkPingResult
  , getBulkPingStatus
  , postBulkPingCancel
  , postBulkPingStart
  ) where

import Prelude

import API.Util (mkClient)
import Data.Argonaut (Json, jsonNull)
import Data.BasicURL (BasicURL)
import Data.BasicURL as BasicUrl
import Data.Bifunctor (bimap, lmap)
import Data.Codec.Argonaut (JsonCodec, JsonDecodeError, (>~>))
import Data.Codec.Argonaut (codec, decode, encode, json, string, prismaticCodec) as CA
import Data.Codec.Argonaut as CJ
import Data.Codec.Argonaut.Common as CJC
import Data.Codec.Argonaut.Compat as CAC
import Data.Codec.Argonaut.Record as CAR
import Data.Codec.Argonaut.Record as CJR
import Data.DateTime (DateTime)
import Data.DateTime.Parsing (FullDateTime(..))
import Data.DateTime.Parsing as DateTimeParse
import Data.Duration (Duration)
import Data.Duration as Duration
import Data.Either (Either(..))
import Data.Generic.Rep (class Generic)
import Data.HTTP.Method (Method(..))
import Data.Map (Map)
import Data.Maybe (Maybe(..))
import Data.Newtype (un)
import Data.Show.Generic (genericShow)
import Data.Time.Duration (Milliseconds(..))
import Data.Time.Duration as DTD
import Effect.Aff (Aff)
import Parsing (parseErrorMessage)
import Routing.Duplex (RouteDuplex')
import Routing.Duplex as RD
import Visonum.BulkPingApp.Util (dateTimeToStringISO8601)

type UUIDv4 = String

type IP = String

type BulkPingConfig =
  { agentUrl :: BasicURL
  , pingCountPerIp :: Int
  , intervalBetweenPingsMillis :: Duration
  , intervalBetweenIpsMillis :: Duration
  , useSynToPort :: Maybe Int
  , useIcmp :: Boolean
  }

codecBulkPingConfig :: JsonCodec BulkPingConfig
codecBulkPingConfig = CJR.object
  "BulkPingConfig"
  { pingCountPerIp: CJ.int
  , agentUrl: codecBasicURL
  , intervalBetweenPingsMillis: codecDurationMillis
  , intervalBetweenIpsMillis: codecDurationMillis
  , useSynToPort: CAC.maybe CJ.int
  , useIcmp: CJ.boolean
  }

-------------------------------------------------------------------------------
-- BulkPingStart 
-------------------------------------------------------------------------------

type BulkPingStartRequest =
  { destinationIps :: Array IP
  , config :: BulkPingConfig
  }

type BulkPingStartResponse =
  { jobId :: UUIDv4
  }

postBulkPingStart :: BasicURL -> String -> BulkPingStartRequest -> Aff (Either String BulkPingStartResponse)
postBulkPingStart baseUrl token body = mkClient
  { baseUrl
  , method: POST
  , path: "v1/vf/bulk-ping/start"
  , token: Just token
  , query: Nothing
  , body: Just body
  , encodeBody: CJ.encode codecBulkPingStartRequest
  , decodeResponse: CJ.decode codecBulkPingStartResponse
  , encodeQuery: mempty
  }

codecBulkPingStartRequest :: JsonCodec BulkPingStartRequest
codecBulkPingStartRequest = CJR.object
  "BulkPingStartRequest"
  { destinationIps: CJ.array CJ.string
  , config: codecBulkPingConfig
  }

codecBulkPingStartResponse :: JsonCodec BulkPingStartResponse
codecBulkPingStartResponse = CJR.object
  "BulkPingStartResponse"
  { jobId: CJ.string
  }

-------------------------------------------------------------------------------
-- BulkPingCancel
-------------------------------------------------------------------------------

type BulkPingCancelRequest =
  { jobId :: UUIDv4
  }

type BulkPingCancelResponse = {}

postBulkPingCancel :: BasicURL -> String -> BulkPingCancelRequest -> Aff (Either String BulkPingCancelResponse)
postBulkPingCancel baseUrl token body = mkClient
  { baseUrl
  , method: POST
  , path: "v1/vf/bulk-ping/cancel"
  , token: Just token
  , query: Nothing
  , body: Just body
  , encodeBody: CJ.encode codecBulkPingCancelRequest
  , decodeResponse: CJ.decode codecBulkPingCancelResponse
  , encodeQuery: mempty
  }

codecBulkPingCancelRequest :: JsonCodec BulkPingCancelRequest
codecBulkPingCancelRequest = CJR.object
  "BulkPingCancelRequest"
  { jobId: CJ.string
  }

codecBulkPingCancelResponse :: JsonCodec BulkPingCancelResponse
codecBulkPingCancelResponse = CJR.object
  "BulkPingCancelResponse"
  {}

-------------------------------------------------------------------------------
-- BulkPingStatus
-------------------------------------------------------------------------------

type BulkPingStatusQuery = {
  c :: Int
}

type BulkPingStatusResponse =
  { jobs :: Map UUIDv4 BulkPingJobStatusEntry
  }

type BulkPingJobStatusEntry =
  { status :: BulkPingJobStatus
  , processedIpCount :: Int
  , initialIpCount :: Int
  , errorsCount :: Int
  , startedAt :: DateTime
  }

data BulkPingJobStatus
  = Pending
  | Running
  | Cancelled
  | Finished
  | Failed

derive instance Generic BulkPingJobStatus _

instance Show BulkPingJobStatus where
  show = genericShow

getBulkPingStatus :: BasicURL -> String -> BulkPingStatusQuery -> Aff (Either String BulkPingStatusResponse)
getBulkPingStatus baseUrl token query = mkClient
  { baseUrl
  , method: GET
  , path: "v1/vf/bulk-ping/status"
  , token: Just token
  , query: Just query
  , body: Nothing
  , encodeBody: const jsonNull
  , encodeQuery: RD.print rdBulkPingStatusQuery
  , decodeResponse: CJ.decode codecBulkPingStatusResponse
  }

codecBulkPingStatusResponse :: JsonCodec BulkPingStatusResponse
codecBulkPingStatusResponse = CJR.object
  "BulkPingStatusResponse"
  { jobs: CJC.strMap codecJobStatusEntry
  }

codecJobStatusEntry :: JsonCodec BulkPingJobStatusEntry
codecJobStatusEntry = CJR.object
  "BulkPingJobStatusEntry"
  { status: codecJobStatus
  , processedIpCount: CJ.int
  , initialIpCount: CJ.int
  , errorsCount: CJ.int
  , startedAt: codecDateTime
  }


codecJobStatus :: JsonCodec BulkPingJobStatus
codecJobStatus = CA.prismaticCodec "BulkPingJobStatus" decode encode CA.string
  where
    decode = case _ of
      "Pending" -> Just Pending
      "Running" -> Just Running
      "Cancelled" -> Just Cancelled
      "Finished" -> Just Finished
      "Failed" -> Just Failed
      _ ->  Nothing

    encode = case _ of
      Pending -> "Pending"
      Running -> "Running"
      Cancelled -> "Cancelled"
      Finished -> "Finished"
      Failed -> "Failed"

rdBulkPingStatusQuery :: RouteDuplex' BulkPingStatusQuery
rdBulkPingStatusQuery = RD.params
  { c: RD.int
  }

-------------------------------------------------------------------------------
-- BulkPingResult
-------------------------------------------------------------------------------

type BulkPingResultQuery =
  { jobId :: UUIDv4
  , c :: Int
  }

type BulkPingResultResponse =
  { config :: BulkPingConfig
  , results :: Array BulkPingEntry
  }

type BulkPingEntry =
  { destination :: IP
  , timeSyn :: Maybe BulkPingResult
  , timeIcmp :: Maybe BulkPingResult
  }

data BulkPingResult
  = BulkPingSuccess
      { pingTimeMinMillis :: Duration
      , pingTimeAvgMillis :: Duration
      , pingTimeMaxMillis :: Duration
      , pingTimeStdDevJitterMillis :: Duration
      , pingTimeTR143JitterMillis :: Duration
      , pingTimeMedianMillis :: Duration
      , timestamp :: DateTime
      }
  | BulkPingError { message :: String }

derive instance Generic BulkPingResult _

instance Show BulkPingResult where
  show = genericShow

codecBulkPingResult :: JsonCodec BulkPingResult
codecBulkPingResult = CA.codec decode encode
  where
  encode :: BulkPingResult -> Json
  encode = case _ of
    BulkPingSuccess r -> CA.encode codecSuccessFields r
    BulkPingError r -> CA.encode codecErrorFields r

  decode :: Json -> Either JsonDecodeError BulkPingResult
  decode json = do
    obj <- CA.decode codecObj json
    case obj.tag of
      "BulkPingSuccess" -> case obj.payload of
        Just payload -> BulkPingSuccess <$> CA.decode codecSuccessFields payload
        Nothing -> Left $ CJ.TypeMismatch "BulkPingResult"
      "BulkPingError" -> case obj.payload of
        Just payload -> BulkPingError <$> CA.decode codecErrorFields payload
        Nothing -> Left $ CJ.TypeMismatch "BulkPingResult"
      _ -> Left $ CJ.TypeMismatch "BulkPingResult"

  codecSuccessFields = CJR.object
    "BulkPingSuccess"
    { pingTimeMinMillis: codecDurationMillis
    , pingTimeAvgMillis: codecDurationMillis
    , pingTimeMaxMillis: codecDurationMillis
    , pingTimeStdDevJitterMillis: codecDurationMillis
    , pingTimeTR143JitterMillis: codecDurationMillis
    , pingTimeMedianMillis: codecDurationMillis
    , timestamp: codecDateTime
    }

  codecErrorFields = CJR.object
    "BulkPingError"
    { message: CJ.string
    }

codecObj :: JsonCodec { tag :: String, payload :: Maybe Json }
codecObj = CAR.object
  "TaggedObject"
  { tag: CA.string
  , payload: CAR.optional CA.json
  }

getBulkPingResult :: BasicURL -> String -> BulkPingResultQuery -> Aff (Either String BulkPingResultResponse)
getBulkPingResult baseUrl token query = mkClient
  { baseUrl
  , method: GET
  , path: "v1/vf/bulk-ping/result"
  , token: Just token
  , query: Just query
  , body: Nothing
  , encodeBody: const jsonNull
  , encodeQuery: RD.print rdBulkPingResultQuery
  , decodeResponse: CJ.decode codecBulkPingResultResponse
  }

rdBulkPingResultQuery :: RouteDuplex' BulkPingResultQuery
rdBulkPingResultQuery = RD.params
  { jobId: RD.string
  , c: RD.int
  }

codecBulkPingResultResponse :: JsonCodec BulkPingResultResponse
codecBulkPingResultResponse = CJR.object
  "BulkPingResultResponse"
  { config: codecBulkPingConfig
  , results: CJ.array codecBulkPingEntry
  }

codecBulkPingEntry :: JsonCodec BulkPingEntry
codecBulkPingEntry = CJR.object
  "BulkPingEntry"
  { destination: CJ.string
  , timeSyn: CAC.maybe codecBulkPingResult
  , timeIcmp: CAC.maybe codecBulkPingResult
  }

-------------------------------------------------------------------------------
-- Codecs
-------------------------------------------------------------------------------

codecBasicURL :: JsonCodec BasicURL
codecBasicURL = CJ.string >~> CJ.codec decode encode
  where
  decode :: String -> Either CJ.JsonDecodeError BasicURL
  decode = BasicUrl.parse >>> lmap (show >>> CJ.TypeMismatch)

  encode :: BasicURL -> String
  encode = BasicUrl.print

codecDurationMillis :: JsonCodec Duration
codecDurationMillis = CJ.number >~> CJ.codec decode encode
  where
  decode :: Number -> Either CJ.JsonDecodeError Duration
  decode = Duration.fromMs >>> pure

  encode :: Duration -> Number
  encode = un Milliseconds <<< DTD.convertDuration

codecDateTime :: JsonCodec DateTime
codecDateTime = CA.string >~> CJ.codec decode dateTimeToStringISO8601
  where
  decode :: String -> Either CJ.JsonDecodeError DateTime
  decode = DateTimeParse.fromString >>>
    bimap
      (parseErrorMessage >>> CJ.TypeMismatch)
      (\(FullDateTime dt _) -> dt)
